<template>
  <div class="sidebar">
    <div class="h-full flex flex-col min-h-0">
      <component :is="isDeactivated ? 'div' : 'router-link'"
                 exact
                 to="/"
                 class="amio-logo"
      >
        <img :src="amioLogo" alt="amio logo" class="h-12">
      </component>

      <div v-bar class="flex-1 min-h-0 overflow-y-auto">
        <div class="min-h-full flex flex-col items-center justify-between">
          <!-- Top group: Answers -> History -->
          <div>
            <component v-for="({ name, icon }, i) in navigationItems"
                       :is="isDeactivated ? 'div' : 'vx-tooltip'"
                       :key="i"
                       position="right"
                       :text="resolveTooltipText(name)"
                       :enabled="isDisabled(name)"
            >
              <component :key="name"
                         :is="isBotLoading || isDisabled(name)  ? 'div' : 'router-link'"
                         ref="sidebarItem"
                         :class="linkClasses(i, name)"
                         :to="resolveNavTo(name)"
                         active-class="router-link-active"
                         class="sidebar-item"
              >
                <component :is="icon"
                           :class="{ 'opacity-50': selectedItemIndex !== i }"
                           :is-filled="selectedItemIndex === i"
                />
                {{ name }}
              </component>
            </component>
          </div>

          <!-- Bottom group: Settings + Logout -->
          <div>
            <div>
              <component :is="isBotLoading || isDeactivated ? 'div' : 'router-link'"
                         :class="settingsLinkClasses()"
                         :to="{ name: ROUTE_NAMES.settings, params: { botId } }"
                         active-class="router-link-active"
                         class="sidebar-item border-solid border-0 border-t border-soft"
              >
                <settings-icon :class="{ 'opacity-50': !$route.path.includes(ROUTE_NAMES.settings) }"
                               :is-filled="$route.path.includes(ROUTE_NAMES.settings)"
                />
                {{ ROUTE_NAMES.settings }}
              </component>
            </div>

            <router-link class="sidebar-item border-solid border-0 border-t border-soft"
                         to="/logout"
            >
              <logout-icon class="opacity-50"/>
              {{ ROUTE_NAMES.logout }}
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AnalyticsIcon from '@/assets/images/icons/AnalyticsIcon'
import BlocksIcon from '@/assets/images/icons/BlocksIcon'
import ChannelsIcon from '@/assets/images/icons/ChannelsIcon'
import EventsIcon from '@/assets/images/icons/EventsIcon'
import AnswersIcon from '@/assets/images/icons/AnswersIcon'
import ToolsIcon from '@/assets/images/icons/ToolsIcon'
import HistoryIcon from '@/assets/images/icons/HistoryIcon'
import LogoutIcon from '@/assets/images/icons/LogoutIcon'
import SettingsIcon from '@/assets/images/icons/SettingsIcon.vue'
import { ROUTE_NAMES } from '@/router'
import amioLogo from '@/assets/images/logo/amio.svg'
import botModule from '@/store/modules/bot.module'
import isEmpty from 'ramda/src/isEmpty'
import stringUtils from '@/utils/string.utils'
import { getLogicalBotId } from '@/utils/market.utils'

export default {
  name: 'AmioSidebar',

  components: {
    ToolsIcon,
    HistoryIcon,
    EventsIcon,
    AnalyticsIcon,
    ChannelsIcon,
    BlocksIcon,
    AnswersIcon,
    LogoutIcon,
    SettingsIcon
  },

  props: {
    isDeactivated: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      amioLogo,
      selectedItemIndex: 0
    }
  },

  computed: {
    isBotLoading: botModule.getBotLoading,
    botList: botModule.getBotList,
    bot: botModule.getBot,
    queryBots() {
      // Preserve current bot set encoded in the URL (needed for reload/state consistency).
      if (typeof this.$route.query.bots !== 'undefined') return this.$route.query.bots
      const logicalBotId = getLogicalBotId(this.bot)
      return logicalBotId || null
    },
    botId() {
      const routeBotId = this.$route.params.botId
      return routeBotId || botModule.getPrimaryBotId(routeBotId) || this.bot?.id
    },
    
    isEmptyBotList() {
      return isEmpty(this.botList)
    },

    navigationItems() {
      return [
        {
          name: ROUTE_NAMES.answers,
          icon: 'answers-icon'
        },
        {
          name: ROUTE_NAMES.events,
          icon: 'events-icon'
        },
        {
          name: ROUTE_NAMES.blocks,
          icon: 'blocks-icon'
        },
        {
          name: ROUTE_NAMES.tools,
          icon: 'tools-icon'
        },
        {
          name: ROUTE_NAMES.analytics,
          icon: 'analytics-icon'
        },
        {
          name: ROUTE_NAMES.history,
          icon: 'history-icon'
        }
      ]
    }
  },

  methods: {
    isDisabled(name) {
      if (this.isDeactivated) return true
      // Tools is org-scoped (see resolveNavTo below) — unlike every other item here, it needs no bot
      // to exist, so an empty bot list must not disable it.
      if (name === ROUTE_NAMES.tools) return false
      return this.isEmptyBotList
    },

    resolveTooltipText(name) {
      if (name === ROUTE_NAMES.tools) return ''
      return `${stringUtils.capitalizeFirstChar(name)} will be available after a bot has been created`
    },

    resolveNavTo(name) {
      // Tools are org-scoped, so the route carries no botId.
      if (name === ROUTE_NAMES.tools) return { name }
      if (name === ROUTE_NAMES.analytics || name === ROUTE_NAMES.history) {
        return { name, query: this.queryBots !== null ? { bots: this.queryBots } : {} }
      }
      return { name, params: { botId: this.botId } }
    },

    linkClasses(i, name) {
      const applyTopBorder = i !== 0 && this.selectedItemIndex !== i && this.selectedItemIndex + 1 !== i
      const classes = [
        { 'border-solid border-0 border-t border-soft': applyTopBorder },
        { 'router-link-active': this.selectedItemIndex === i }
      ]

      if (this.isDisabled(name)) {
        classes.push('cursor-not-allowed text-gray hover:text-gray')
        return classes
      }

      if (this.isBotLoading) {
        classes.push('cursor-loading')
      } else {
        classes.push('cursor-pointer')
      }

      return classes
    },

    settingsLinkClasses() {
      const classes = []
      if (this.isDeactivated) {
        classes.push('cursor-not-allowed text-gray hover:text-gray')
        return classes
      }

      if (this.isBotLoading) {
        classes.push('cursor-loading')
      } else {
        classes.push('cursor-pointer')
      }
      return classes
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        this.selectedItemIndex = this.navigationItems.findIndex(item => path.includes(item.name))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.amio-logo {
  height: 100px;
  @apply flex justify-center items-center;
}

.sidebar {
  width: 100px;
  @apply h-full fixed top-0 left-0 bg-white flex flex-col items-center justify-between text-center px-3 z-20;

  &-item {
    @apply flex flex-col justify-center items-center capitalize w-23 h-23 gap-2 text-gray-dark;
    @apply hover:text-black hover:opacity-100 text-sm font-medium;
  }
}

.router-link-active {
  background-color: theme('colors.primary-light');
  box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  @apply rounded w-23 h-23 text-primary hover:text-primary opacity-100;
}
</style>
